import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import {
  SettlementLineItemGrouping,
  SettlementLineItemType,
} from '@treadinc/horizon-api-spec';
import { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';

import { CheckBoxFormField } from '~components/FormFields/CheckBoxFormField';
import { RadioGroupFormField } from '~components/FormFields/RadioGroupFormField';
import DateInput from '~components/FormFields/v2/DateInput';
import SelectInput from '~components/FormFields/v2/SelectInput';
import TextInput from '~components/FormFields/v2/TextInput';
import { Settlement, SettlementLine } from '~hooks/useSettlements/models';
import useSettlementPaymentTerm from '~hooks/useSettlements/useSettlementPaymentTerm';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { NO_PAYMENT_TERM } from '~utils/settlements/paymentTerms';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

const settlementLineItemTypeValues = [
  SettlementLineItemType.JOB,
  SettlementLineItemType.LOAD,
];

const settlementLineItemTypeList = [
  {
    value: SettlementLineItemType.JOB,
    label: (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="body2">
          {t('approvals.driver_pay.settlements.line_item_type.job')}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('approvals.driver_pay.settlements.line_item_type.job_description')}
        </Typography>
      </Box>
    ),
  },
  {
    value: SettlementLineItemType.LOAD,
    label: (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="body2">
          {t('approvals.driver_pay.settlements.line_item_type.load')}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('approvals.driver_pay.settlements.line_item_type.load_description')}
        </Typography>
      </Box>
    ),
  },
];

const settlementLineItemGroupingList = Object.values(SettlementLineItemGrouping);

const setGroupingsFormFieldDefaultValue = (list: SettlementLineItemGrouping[] = []) => {
  return settlementLineItemGroupingList.reduce(
    (obj, key) => {
      obj[key] = list.includes(key);
      return obj;
    },
    {} as Record<SettlementLineItemGrouping, boolean>,
  );
};

export const setSettlementSchemaDefaultValues = (
  invoiceIds?: string[],
  settlement?: Settlement,
  lines?: SettlementLine[],
) => {
  return {
    id: settlement?.id ?? null,
    settlementLineItemType: settlement?.settlementLineItemType ?? null,
    settlementLineItemGroupings: setGroupingsFormFieldDefaultValue(
      settlement?.settlementLineItemGroupings,
    ),
    includeScaleTickets: false,
    allLoadsAndTicketNumbers: settlement?.includeLoadsInPdf ?? false,
    invoiceIds: invoiceIds ?? null,
    lines: lines ?? null,
    dueDate: settlement?.dueDate ?? null,
    // the notes textarea is controlled, so an absent value must be '' rather than null
    notes: settlement?.notes ?? '',
    editable: settlement?.editable ?? true,
  } as unknown as SettlementDTO;
};

export type SettlementDTO = {
  id: string | null;
  settlementLineItemType: SettlementLineItemType;
  settlementLineItemGroupings: Record<SettlementLineItemGrouping, boolean>;
  includeScaleTickets: boolean;
  allLoadsAndTicketNumbers: boolean;
  invoiceIds: string[] | null;
  lines?: SettlementLine[] | null;
  // Optional so a caller editing just one of them can omit the other and leave it
  // untouched; `null` still means "clear it". See Settlement.deparseUpdate.
  dueDate?: Dayjs | null;
  notes?: string | null;
  // Omitted unless the caller is editing the payment term: these clear the term on the
  // Rutter export row when sent, so a save that just resends defaults must leave them out.
  externalPaymentTermsId?: string | null;
  paymentTermsName?: string | null;
  paymentTermsDueDays?: number | null;
  // Omitted unless the caller is editing the Tread payment term; `null` clears it.
  paymentTermId?: string | null;
  // Mirrors the BE-computed Settlement#editable flag: an approved (locked) settlement
  // rejects due_date/notes, so this gates whether they render enabled and travel in the
  // update payload.
  editable: boolean;
};

export type SettlementFormFieldsHandler = {
  onSubmit?: (callback: (args: SettlementDTO) => void) => void;
};

interface SettlementFormFieldsProps {
  invoiceIds?: string[];
  lines?: SettlementLine[];
  settlement?: Settlement;
  allowEmptyInvoiceIds?: boolean;
  // lets the host hold submit while a new settlement awaits its default term
  onPaymentTermsLoadingChange?: (isLoading: boolean) => void;
}

export const SettlementFormFields = forwardRef<
  SettlementFormFieldsHandler,
  SettlementFormFieldsProps
>(function SettlementFormFields(
  {
    invoiceIds,
    lines,
    settlement,
    allowEmptyInvoiceIds = false,
    onPaymentTermsLoadingChange,
  },
  ref,
) {
  const isInvoiceIdsOptional = allowEmptyInvoiceIds || Boolean(lines?.length);

  const dynamicSchema = yup.object().shape({
    id: yup.string().trim().uuid().notRequired(),
    settlementLineItemType: yup
      .mixed<SettlementLineItemType>()
      .oneOf(settlementLineItemTypeValues)
      .required(`${t('form_validation_errors.required_simple')}`),
    settlementLineItemGroupings: yup
      .object()
      .shape({
        [SettlementLineItemGrouping.EQUIPMENT_TYPE]: yup.boolean(),
        [SettlementLineItemGrouping.MATERIAL]: yup.boolean(),
        [SettlementLineItemGrouping.SERVICE]: yup.boolean(),
      })
      .notRequired(),
    includeScaleTickets: yup.boolean(),
    allLoadsAndTicketNumbers: yup.boolean(),
    invoiceIds: yup
      .array()
      .of(yup.string().trim().min(1).required())
      .when('id', {
        is: (id: Nullable<string>) => isNil(id),
        then: (schema) =>
          isInvoiceIdsOptional ? schema.notRequired() : schema.min(1).required(),
        otherwise: (schema) => schema.notRequired(),
      }),
    lines: yup
      .array()
      .of(
        yup.object({
          id: yup.string().required(),
          kind: yup.mixed<SettlementLine['kind']>().required(),
        }),
      )
      .nullable()
      .notRequired(),
    dueDate: yup
      .mixed<Dayjs>()
      .nullable()
      .notRequired()
      // A half-typed date reaches the form as an invalid Dayjs, which would otherwise
      // serialize to the literal string "Invalid Date" in the payload.
      .test('due-date-is-valid', t('form_validation_errors.invalid_date'), (value) =>
        value ? value.isValid() : true,
      ),
    notes: yup
      .string()
      .trim()
      // Matches the settlements.notes column limit enforced by the API
      .max(
        1000,
        `${t('form_validation_errors.max_length', { field: t('form_fields.notes'), max: 1000 })}`,
      )
      .nullable()
      .notRequired(),
    editable: yup.boolean(),
    paymentTermId: yup.string().nullable().notRequired(),
  });

  const savedPaymentTermId = settlement?.paymentTermId ?? NO_PAYMENT_TERM;

  const { userStore } = useStores();

  const {
    control,
    formState: { errors },
    getFieldState,
    getValues,
    handleSubmit,
    resetField,
    setValue,
  } = useForm<SettlementDTO>({
    resolver: yupResolver(dynamicSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      ...setSettlementSchemaDefaultValues(invoiceIds, settlement, lines),
      paymentTermId: savedPaymentTermId,
    },
  });

  const watchedSettlementLineItemType = useWatch({
    control,
    name: 'settlementLineItemType',
  });

  const settlementIsEditable = settlement?.editable ?? true;

  useEffect(() => {
    if (watchedSettlementLineItemType === SettlementLineItemType.LOAD) {
      setValue('allLoadsAndTicketNumbers', false);
    }
  }, [watchedSettlementLineItemType, setValue]);

  const {
    isFetchingPaymentTerms,
    isWaitingForDefaultPaymentTerm,
    paymentTermOptions,
    toSubmitPayload,
  } = useSettlementPaymentTerm({
    companyId: getEffectiveUserCompanyId(userStore),
    control,
    getFieldState,
    getValues,
    resetField,
    savedPaymentTermId,
    setValue,
    settlement,
  });

  useEffect(() => {
    onPaymentTermsLoadingChange?.(isWaitingForDefaultPaymentTerm);
  }, [isWaitingForDefaultPaymentTerm, onPaymentTermsLoadingChange]);

  useImperativeHandle(
    ref,
    () => ({
      onSubmit(callback) {
        handleSubmit((values) => callback(toSubmitPayload(values)))();
      },
    }),
    [handleSubmit, toSubmitPayload],
  );

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <Box>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
          }}
        >
          {t('approvals.driver_pay.settlements.itemize_settlement_by')}
        </Typography>

        <RadioGroupFormField
          control={control}
          errors={errors}
          list={settlementLineItemTypeList}
          name="settlementLineItemType"
          radioGroupSx={{
            flexDirection: 'column',
            '& .MuiRadio-root': { py: 0.5 },
          }}
          sx={{ mt: 2 }}
        />
      </Box>
      <Divider />
      <Box>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
          }}
        >
          {t('approvals.driver_pay.settlements.pdf_options')}
        </Typography>

        <Box
          sx={{
            mt: 2,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
            }}
          >
            {t('approvals.driver_pay.settlements.line_item_grouping')}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              mt: 1,
            }}
          >
            {settlementLineItemGroupingList.map((value) => (
              <CheckBoxFormField
                key={value}
                control={control}
                errors={errors}
                label={`${t(`approvals.driver_pay.settlements.grouping.${value}`)}`}
                name={`settlementLineItemGroupings.${value}`}
                value={false}
                checkboxSx={{ '&.MuiCheckbox-root': { py: 0.5 } }}
              />
            ))}
          </Box>
        </Box>

        {watchedSettlementLineItemType !== SettlementLineItemType.LOAD && (
          <Box
            sx={{
              mt: 2,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
              }}
            >
              {t('approvals.driver_pay.settlements.additional_pages')}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                mt: 1,
              }}
            >
              <CheckBoxFormField
                control={control}
                errors={errors}
                label={`${t('approvals.driver_pay.settlements.all_loads_and_ticket_numbers')}`}
                name="allLoadsAndTicketNumbers"
                value={false}
                checkboxSx={{ '&.MuiCheckbox-root': { py: 0.5 } }}
              />
            </Box>
          </Box>
        )}
      </Box>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <SelectInput
          control={control}
          displayEmpty
          isDisabled={isFetchingPaymentTerms || !settlementIsEditable}
          label={t('form_fields.payment_terms')}
          name="paymentTermId"
          options={paymentTermOptions}
        />
        <DateInput
          control={control}
          isClearable
          isDisabled={!settlementIsEditable}
          label={t('form_fields.due_date')}
          name="dueDate"
        />
        <TextInput
          control={control}
          isDisabled={!settlementIsEditable}
          label={t('form_fields.notes')}
          name="notes"
          rows={3}
        />
      </Box>
      {/* TRE-11950: Temporarily hiding non-functional "Include Scale Tickets" checkbox */}
      {/*
      <Box>
        <CheckBoxFormField
          checkboxSx={{ '&.MuiCheckbox-root': { py: 0.5 } }}
          control={control}
          errors={errors}
          label={`${t('approvals.driver_pay.settlements.include_scale_tickets')}`}
          name="includeScaleTickets"
          value={false}
        />
      </Box>
      */}
    </Box>
  );
});
